import type { BookingMessages } from "@/i18n/types";
import { de } from "@/i18n/de";
import type { OnlineBookingMinStayPeriod, OnlineBookingMinStaySettings } from "@/types";
import { isDateInBookingSeason } from "@/lib/bookingSeasons";
import { nightsBetween } from "@/lib/dates";

export type MinStayValidationMessages = Pick<
  BookingMessages["validation"],
  "minStayOne" | "minStayMany"
>;

function periodContainsDate(iso: string, period: OnlineBookingMinStayPeriod): boolean {
  return isDateInBookingSeason(iso, period);
}

function requiredMinNights(
  arrival: string,
  departure: string,
  config: OnlineBookingMinStaySettings,
): number {
  if (!config.enabled) return 1;
  if (!arrival || !departure || departure <= arrival) {
    return Math.max(1, config.default_min_nights);
  }
  if (config.periods.length === 0) {
    return Math.max(1, config.default_min_nights);
  }

  const matching: number[] = [];
  let current = arrival;
  while (current < departure) {
    for (const period of config.periods) {
      if (periodContainsDate(current, period)) {
        matching.push(Math.max(1, period.min_nights));
      }
    }
    const [y, m, d] = current.split("-").map(Number);
    const next = new Date(y, m - 1, d + 1);
    const ny = next.getFullYear();
    const nm = String(next.getMonth() + 1).padStart(2, "0");
    const nd = String(next.getDate()).padStart(2, "0");
    current = `${ny}-${nm}-${nd}`;
  }

  if (matching.length > 0) return Math.max(...matching);
  return Math.max(1, config.default_min_nights);
}

export function validateMinStay(
  arrival: string,
  departure: string,
  config: OnlineBookingMinStaySettings | undefined,
  messages: MinStayValidationMessages = de.validation,
): string | null {
  if (!config?.enabled) return null;
  if (!arrival || !departure) return null;
  if (departure <= arrival) return null;

  const nights = nightsBetween(arrival, departure);
  const required = requiredMinNights(arrival, departure, config);
  if (nights >= required) return null;
  if (required === 1) return messages.minStayOne;
  return messages.minStayMany(required);
}
